import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../../components/ui/button.tsx";
import { Checkbox } from "../../components/ui/checkbox.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog.tsx";
import { Input } from "../../components/ui/input.tsx";
import { Label } from "../../components/ui/label.tsx";
import { Textarea } from "../../components/ui/textarea.tsx";
import { errorToast } from "../../components/ui/toast.tsx";
import { useCurrentUserContext } from "../../hooks/useUser.tsx";
import { stashToast } from "../../lib/deferredToast.ts";
import { clearClientStorage, endSession } from "@/lib/endSession.ts";
import { api } from "@/lib/http.ts";
import { deleteUserAccount } from "../../lib/user.ts";
import type { SwitchAccountReq, SwitchAccountResp } from "../../types/accounts.ts";

export const DeleteAccount = () => {
  const { userContext } = useCurrentUserContext();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");

  const accounts = userContext?.user.accounts ?? [];
  const currentAccountId = userContext?.user.id;
  const currentAccount = accounts.find((a) => a.id === currentAccountId);
  const remainingAccount = accounts.find((a) => a.id !== currentAccountId);
  const isLastAccount = accounts.length <= 1;

  const canSubmit = confirmed && confirmText.toUpperCase() === "DELETE";

  const deleteMutation = useMutation({
    mutationFn: async (leavingReason: string) => {
      await deleteUserAccount("DELETE", leavingReason);
      if (!isLastAccount && remainingAccount) {
        // Switch to the remaining account before reloading, otherwise the
        // next page load would hit an invalid bba cookie (pointing to the
        // just-deleted account) and auto-resolve to the first remaining
        // account — same destination, but an extra round-trip and no toast.
        //
        // If the switch call fails, the delete itself already succeeded —
        // the auth middleware's auto-resolve fallback will pick a remaining
        // account on reload. Swallow the error and fall through to onSuccess
        // rather than showing a misleading "failed to delete" toast.
        try {
          await api.post<SwitchAccountResp, SwitchAccountReq>("/api/a/users/switch-account", {
            accountId: remainingAccount.id,
          });
        } catch {
          // Swallow — reload will auto-resolve to a remaining account.
        }
      }
    },
    onSuccess: () => {
      if (isLastAccount) {
        // The server has invalidated the session; this is the logout wipe.
        endSession("/auth");
        return;
      }
      // Web storage is not partitioned by account, so everything in it may
      // belong to the account just deleted: wipe it all, then stash the toast
      // for the sibling the reload lands in.
      clearClientStorage();
      stashToast({
        kind: "success",
        message: currentAccount?.displayName
          ? `Deleted ${currentAccount.displayName}.`
          : "Account deleted.",
      });
      window.location.reload();
    },
    onError: () => {
      errorToast("Failed to delete account. Please try again.");
    },
  });

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setConfirmText("");
      setConfirmed(false);
      setReason("");
    }
  };

  const dialogTitle = isLastAccount ? "Delete Account" : "Delete This Account";
  const dialogDescription = isLastAccount
    ? "This is your only account. Deleting it will permanently remove your profile and all associated data — emails, calendar events, people, and conversation history. This action cannot be undone."
    : `This will permanently delete ${currentAccount?.displayName ?? "this account"} and all its data — emails, calendar events, people, and conversation history. You'll be switched to ${remainingAccount?.displayName ?? "your remaining account"}. This action cannot be undone.`;
  const buttonLabel = deleteMutation.isPending
    ? "Deleting..."
    : isLastAccount
      ? "Delete Account"
      : "Delete This Account";

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-statsig-name="settings-delete-account"
        className="text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
      >
        {isLastAccount ? "Delete Account" : "Delete This Account"}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[425px]" hideClose>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <Label htmlFor="delete-reason" className="text-sm">
                Why are you leaving? <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="delete-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={2000}
                rows={3}
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="confirm-delete"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
              />
              <Label htmlFor="confirm-delete" className="text-sm leading-snug cursor-pointer">
                I understand this action cannot be undone
              </Label>
            </div>

            <div className="space-y-3">
              <Label htmlFor="confirm-text" className="text-sm">
                Type <span className="font-semibold">DELETE</span> to confirm
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              data-statsig-name="settings-delete-account-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!canSubmit || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(reason)}
              data-statsig-name="settings-delete-account-confirm"
            >
              {buttonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
